import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { LocalStorageService } from 'angular-2-local-storage';
import { ToastrService } from 'ngx-toastr';
import { APIService } from '../../providers/api.service';

/**
 * SG-177 Phase 10 (review-round fix, controller ruling): thin redirect-only
 * landing for the invitation email's deep link.
 *
 * PrivateContestServiceImpl#generateDeepLink / #buildDeepLink (backend,
 * Phase 4/5/9) always builds the invitation email's link -- for BOTH
 * existing- and new-user invites, and for Phase 5's post-registration
 * inviteResult.redirectUrl -- as
 * `{mail.host.url}user/contest-selection?inviteCode={code}&contestId={id}`.
 * That route did not exist anywhere in this Angular app (grep-confirmed
 * zero references before this fix) -- flagged as a routing gap in
 * phase-10-report.md and folded into this review round.
 *
 * This component is deliberately NOT nested under UserDashboardComponent
 * (unlike lobby/makeStatement/etc.) so it renders standalone with no
 * dashboard shell flash for an unauthenticated visitor, and is registered
 * as a plain top-level route (see app-routing.module.ts) ahead of the
 * lazy-loaded 'user' module.
 *
 * Auth-state check: this app has no route guards anywhere (grep-confirmed)
 * -- the established, existing pattern (signup.component.ts's own
 * ngOnInit, APIService#doApiCall's 401 handling) is a direct
 * LocalStorageService 'email'/'token' presence check, reused here rather
 * than introducing a new auth-guard concept.
 *
 * - Unauthenticated: redirect to /landing/signup carrying inviteCode
 *   (+ contestId) so Phase 10's existing deep-link prefill (Tasks
 *   10.1/10.2) picks it up exactly as if the email had linked to
 *   /landing/signup directly.
 * - Authenticated (an existing user, per conventions.md D2 -- existing
 *   users are matched by invited_user_id, not email): this is the exact
 *   scenario POST api/invitation/{inviteCode}/accept documents itself for
 *   ("called after the user logs in/registers from the emailed deep
 *   link" -- ContestInvitationController#acceptInvitation) -- call it to
 *   actually join the contest, then navigate to the Selections screen at
 *   /user/makeStatement/:id, id = btoa('id' + contestId) -- the exact
 *   encoding make-statement.component.ts and lobby.component.ts already
 *   use everywhere else in this app for that same route, so this is a
 *   direct reuse of an existing, unambiguous target rather than a new
 *   convention.
 * - Any failure (accept rejected -- already joined/expired/mismatch/full;
 *   or a transport error; or no inviteCode/userId to act on at all) falls
 *   back to the lobby -- contestId is best-effort carried through via the
 *   existing (if currently unconsumed -- see phase-10-report.md)
 *   `inviteContestId1` localStorage convention already used by
 *   signup.component.ts's own canVerify path, rather than stranding the
 *   user on a dead page.
 *
 * SG-177 launch fix (finding 6): that fallback used to be entirely SILENT --
 * an invitee whose acceptance failed for ANY reason (email mismatch, expired
 * code, contest full, already joined, insufficient coins) was simply dropped
 * on /user/lobby with no explanation, which reads as the link being broken.
 * Every fallback path now raises a toast naming the actual reason, reusing
 * the same ngx-toastr mechanism and the same wording as
 * signup.component.ts#_describeInviteAttachError so the two invite entry
 * points explain identical failures identically.
 */
@Component({
  selector: 'app-contest-selection',
  templateUrl: './contest-selection.component.html',
  styleUrls: ['./contest-selection.component.css']
})
export class ContestSelectionComponent implements OnInit {

  constructor(
    private apiService: APIService,
    private router: Router,
    private route: ActivatedRoute,
    private localStorageService: LocalStorageService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    const params = this.route.snapshot.queryParams;
    const inviteCode = params['inviteCode'] || params['code'];
    const contestIdParam = params['contestId'];

    if (!this._isAuthenticated()) {
      const queryParams: any = {};
      if (inviteCode) {
        queryParams.inviteCode = inviteCode;
      }
      if (contestIdParam) {
        queryParams.contestId = contestIdParam;
      }
      this.router.navigate(['/landing/signup'], { queryParams });
      return;
    }

    if (!inviteCode) {
      // Nothing to accept -- land somewhere safe rather than a blank page.
      this.router.navigate(['/user/lobby']);
      return;
    }

    const userId = this.localStorageService.get('userId');
    if (!userId) {
      // Authenticated per token/email, but userId hasn't been cached yet in
      // this session -- rather than adding a second lookup call here, fall
      // back to the lobby (which resolves/caches userId itself on load).
      this._fallbackToLobby(contestIdParam,
        'Please sign in again to open your invitation.');
      return;
    }

    var self = this;
    this.apiService.doApiCall(function (data) {
      if (data && data.StatusCode === 'SG001' && data.Data && data.Data.contestId) {
        // Also the answer when the user had already joined through this
        // invitation -- the server returns the contest rather than an error,
        // so re-opening the link lands on Selections, not a toast.
        self.router.navigate(['/user/makeStatement', btoa('id' + data.Data.contestId)]);
      } else {
        // A rejected accept is a real, explainable outcome -- name it rather
        // than dropping the user on the lobby wondering what happened.
        self._fallbackToLobby(contestIdParam,
          self._describeAcceptError(data && data.StatusCode));
      }
    }, 'api/invitation/' + encodeURIComponent(inviteCode) + '/accept?requestedBy=' + userId, 'POST', null, null, function () {
      self._fallbackToLobby(contestIdParam,
        'We could not reach the server to open your invitation. Please try the link again.');
    });
  }

  /**
   * Mirrors signup.component.ts#_describeInviteAttachError so the same
   * server-side rejection is described the same way whichever invite entry
   * point the user arrived through.
   *
   * The named EligibilityErrorCode values come back as the StatusCode
   * verbatim; the older generic failures come back as SGnnn codes from
   * Constants.Response (verified against
   * InvitationServiceImpl#handleInvitationAcceptance -- it can raise
   * NOT_ENOUGH_MONEY/SG132, NOT_VALID_ID/SG106
   * and NOT_VALID_EMAIL/SG107 in addition to the invite-specific ones).
   * ALREADY_JOINED_CONTEST/SG139 is no longer raised: re-opening the link
   * after joining now succeeds with the contest id.
   */
  private _describeAcceptError(statusCode: string): string {
    const known: { [k: string]: string } = {
      EMAIL_MISMATCH: 'This invite was sent to a different email address, so we could not add you to the contest.',
      INVITE_INVALID: 'This invite code is no longer valid.',
      INVITE_EXPIRED: 'This invite code has expired.',
      CONTEST_FULL: 'This contest is already full.',
      SUBSCRIPTION_REQUIRED: 'A subscription is required to join this contest.',
      SG132: 'You do not have enough coins to join this contest.',
      SG137: 'This contest\'s game has already started, so it can no longer be joined.',
      SG107: 'This invite was sent to a different email address, so we could not add you to the contest.',
      SG106: 'We could not find that invitation.'
    };
    return (statusCode && known[statusCode])
      || 'We could not add you to that private contest.';
  }

  private _isAuthenticated(): boolean {
    const email = this.localStorageService.get('email');
    const token = this.localStorageService.get('token');
    return !!(email && token);
  }

  private _fallbackToLobby(contestIdParam: string, message?: string): void {
    if (contestIdParam) {
      this.localStorageService.add('inviteContestId1', btoa('id' + contestIdParam));
    }
    if (message) {
      // Toastr survives the navigation below, so the user reads it on the
      // lobby rather than watching it disappear with this component.
      this.toastr.warning(message);
    }
    this.router.navigate(['/user/lobby']);
  }
}
